import { KCSDK } from "@/sdk"
import {
  TransferFileListener,
  TransferFileTaskData,
} from "@/sdk/file_transfer_task"
import { formatBytes } from "@/utils/tools"
import Draggable from "@/widgets/Draggable"
import { CloseIcon } from "@chakra-ui/icons"
import { Box, Divider, Flex, Progress, Text } from "@chakra-ui/react"
import React, { CSSProperties, useCallback, useEffect, useState } from "react"

export enum FileTransferType {
  Send = "send",
  Recv = "recv",
}

interface FileTransferListProps {
  type: FileTransferType
  top?: number
  right?: number
  left?: number
  bottom?: number
}

type SetFileTransferListener = (listener: TransferFileListener) => void

// 文件传输列表
export const FileTransferList: React.FC<FileTransferListProps> = (props) => {
  const { type, top = 10, right = 10, left, bottom } = props
  const [items, setItems] = useState<TransferFileTaskData[]>([])
  const removeTask = useCallback((id: number) => {
    setItems((previous) => previous.filter((item) => item.taskId !== id))
  }, [])

  useEffect(() => {
    let isActive = true
    let setListener: SetFileTransferListener
    if (type === FileTransferType.Send) {
      setListener = KCSDK.setSendFileListener
    } else {
      setListener = KCSDK.setRecvFileListener
    }
    setListener({
      // 监听新任务
      onNewTask(task) {
        if (!isActive) return
        // 添加任务
        const item = {
          taskId: task.taskId,
          fileName: task.fileName,
          fileSize: task.fileSize,
        }
        setItems((previous) =>
          previous.some((previousItem) => previousItem.taskId === item.taskId)
            ? previous
            : [...previous, item]
        )
      },
      // 监听传输完成
      onComplete(id) {
        if (!isActive) return
        // 删除完成的任务
        removeTask(id)
      },
    })
    return () => {
      isActive = false
    }
  }, [removeTask, type])

  if (items.length === 0) {
    return <></>
  }

  const buildItem = (data: TransferFileTaskData, i: number) => {
    const item = (
      <FileTransferItem
        key={data.taskId}
        data={data}
        type={type}
        onStop={() => removeTask(data.taskId)}
      />
    )
    const isLast = i === items.length - 1

    if (isLast) {
      return item
    }

    return (
      <React.Fragment key={`item-${data.taskId}`}>
        {item}
        <Divider
          width={"346px"}
          marginTop="4px"
          marginBottom="16px"
          alignSelf={"center"}
        />
      </React.Fragment>
    )
  }

  const buildHeader = () => {
    const headerStyle: CSSProperties = {
      marginLeft: "16px",
      marginTop: "12px",
      color: "#fff",
    }
    if (type === FileTransferType.Send) {
      return (
        <Text key="header" style={headerStyle}>
          文件上传列表
        </Text>
      )
    }
    return (
      <Text key="header" style={headerStyle}>
        文件下载列表
      </Text>
    )
  }

  return (
    <div id="full-screen-unvideo-element-tag">
      <Draggable right={right} top={top} left={left} bottom={bottom}>
        <Flex
          direction="column"
          width="360px"
          borderRadius="10px"
          backgroundColor="#262c31"
        >
          {buildHeader()}
          {items.map(buildItem)}
        </Flex>
      </Draggable>
    </div>
  )
}

interface FileTransferItemProps {
  type: FileTransferType
  data: TransferFileTaskData
  onStop: () => void
}

const FileTransferItem: React.FC<FileTransferItemProps> = (props) => {
  const { taskId, fileName, fileSize } = props.data
  const { onStop, type } = props
  // 当前接收的字节数
  const [curRecv, setCurRecv] = useState(0)
  // 传输速度
  const [speed, setSpeed] = useState(0)

  useEffect(() => {
    const handleProgress = ({
      curRecv,
      speed,
    }: {
      curRecv: number
      speed: number
    }) => {
      setCurRecv(curRecv)
      setSpeed(speed)
    }
    if (type === FileTransferType.Send) {
      // 监听发送进度
      KCSDK.setFileSendProgressListener(taskId, handleProgress)
    } else {
      // 监听传输进度
      KCSDK.setFileRecvProgressListener(taskId, handleProgress)
    }
  }, [taskId, type])

  // 停止传输任务
  const stop = () => {
    KCSDK.stopTransferFile(taskId)
    onStop()
  }

  // 传输进度百分比
  const safeCurRecv = Math.min(curRecv, fileSize)
  const progress =
    fileSize > 0 ? Math.min(100, Math.round((safeCurRecv / fileSize) * 100)) : 0
  const isComplete = safeCurRecv === fileSize

  const progressInfo = `${formatBytes(safeCurRecv)} / ${formatBytes(fileSize)}`
  const speedInfo = `${formatBytes(speed)}/S`

  const infoStyle: CSSProperties = {
    fontSize: "12px",
    color: "#797c80",
    userSelect: "none",
  }
  return (
    <Flex padding="16px" paddingTop="0px" direction="column">
      <Flex marginBottom="6px" justify="space-between" alignItems="center">
        {/* 文件名 */}
        <Text color="#fff" userSelect="none" fontSize="13px">
          {fileName}
        </Text>
        {/* 停止传输按钮 */}
        {isComplete ? null : (
          <div
            id="full-screen-unvideo-element-tag"
            onClick={stop}
            style={{ cursor: "pointer" }}
          >
            <CloseIcon
              width="18px"
              height="18px"
              padding="4px"
              borderRadius={"50%"}
              background={"#4A4A4A"}
              color={"white"}
            />
          </div>
        )}
      </Flex>
      {/* 进度条 */}
      <Progress
        value={Math.round(progress)}
        size="xs"
        colorScheme="blue"
        borderRadius="2px"
      />
      <Box height="4px" />
      <Flex justify="space-between" align="start">
        {/* 传输速度 */}
        <Text style={infoStyle}>{speedInfo}</Text>
        {/* 传输进度 */}
        <Text style={infoStyle}>{progressInfo}</Text>
      </Flex>
    </Flex>
  )
}
